// ── Observability bootstrap — Sentry + PostHog ────────────────────────
// Imported via ES modules in main.jsx. Initialised once. No-op if env vars
// are missing (so local dev without keys is fine).

import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

const env = (typeof import.meta !== 'undefined' && import.meta.env) || {};

// ── Sentry ────────────────────────────────────────────────────────────
if (env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    environment: env.MODE || 'development',
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: 0.2,
    // Session replay OFF by default — privacy + bundle size for alpha
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    beforeSend(event) {
      // Drop the noisy "ResizeObserver loop limit exceeded" Chrome warning
      const msg = event.exception?.values?.[0]?.value || '';
      if (msg.includes('ResizeObserver')) return null;
      return event;
    },
  });
}

// ── PostHog ───────────────────────────────────────────────────────────
if (env.VITE_POSTHOG_KEY) {
  posthog.init(env.VITE_POSTHOG_KEY, {
    api_host:        env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
    // Autocapture clicks + pageviews, but NEVER form-field contents — users
    // paste API keys, strategy code, etc. into inputs and we won't exfiltrate.
    autocapture: {
      capture_form_elements: false,
      element_attribute_ignorelist: ['data-secret', 'data-token'],
    },
    mask_all_text:     true,                     // mask any captured text (extra safety)
    capture_pageview:  false,                    // manual, on hash change
    persistence:       'localStorage+cookie',
    disable_session_recording: true,             // privacy at alpha
    opt_out_capturing_by_default: true,          // no capture until user accepts cookie consent
  });
}

// ── Public surface ────────────────────────────────────────────────────
window.GQ = window.GQ || {};
window.GQ.observability = {
  Sentry,
  posthog,
  // Convenience event tracker — no-op if PostHog isn't initialised
  track(event, props = {}) {
    if (env.VITE_POSTHOG_KEY) posthog.capture(event, props);
  },
  // Identify the signed-in user
  identify(userId, traits = {}) {
    if (env.VITE_POSTHOG_KEY && userId) posthog.identify(userId, traits);
    if (env.VITE_SENTRY_DSN  && userId) Sentry.setUser({ id: userId, ...traits });
  },
  // Page view (call on hash change)
  pageview(page) {
    if (env.VITE_POSTHOG_KEY) posthog.capture('$pageview', { $current_url: location.href, page });
  },
  // Wrap any component in an error boundary (used in app.jsx)
  ErrorBoundary: Sentry.ErrorBoundary,
};

// Surface a `track` shortcut for ergonomic call-sites
window.gqTrack = window.GQ.observability.track;
